import { AlertTriangle, CheckCircle } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { NativeSelect } from '@/lib/base/forms/select/select-native';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays';

import { usePipelineStageMappings } from './hooks/usePipelineStageMappings';
import type {
  PipelineMappingReport,
  PipelineStageMappingRow,
  StageDrift,
} from './types';

interface PipelineStageMappingManagerProps {
  orgId: number | string;
}

// --- Module-scope constants ---

const DRIFT_BADGE_COLOR: Record<
  StageDrift,
  'success' | 'warning' | 'error' | 'gray'
> = {
  ok: 'success',
  renamed: 'warning',
  archived: 'error',
  unmapped: 'error',
};

const DRIFT_BADGE_LABEL: Record<StageDrift, string> = {
  ok: 'Matched',
  renamed: 'Renamed',
  archived: 'Archived',
  unmapped: 'Unmapped',
};

const SELECT_CLASS = 'min-w-0 py-1.5 text-sm pr-10';

const STAGE_ROW_GRID =
  'sm:grid sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1.5fr)_auto] sm:items-center sm:gap-3';

const MOBILE_LABEL_CLASS =
  'mb-1 block text-xs font-medium text-[var(--color-text-tertiary)] sm:hidden';

const EMPTY_PIPELINES: PipelineMappingReport[] = [];

// --- Sub-components ---

interface StageRowProps {
  stage: PipelineStageMappingRow;
  pipelineStageOptions: readonly { value: string; label: string }[];
  isSaving: boolean;
  onUpdate: (ahoyEntityId: string, hubspotStageId: string) => void;
}

function StageRow({
  stage,
  pipelineStageOptions,
  isSaving,
  onUpdate,
}: StageRowProps) {
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!stage.ahoy_entity_id) {
        return;
      }
      onUpdate(stage.ahoy_entity_id, e.target.value);
    },
    [stage.ahoy_entity_id, onUpdate]
  );

  const isUnmapped = stage.drift === 'unmapped';
  const hasDrift = stage.drift !== 'ok';

  if (isUnmapped) {
    return (
      <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-3'>
        <div className='flex items-start gap-2'>
          <AlertTriangle
            className='mt-0.5 h-4 w-4 shrink-0 text-utility-error-600'
            aria-hidden='true'
          />
          <div className='min-w-0 flex-1'>
            <p className='text-sm font-medium text-utility-error-900'>
              Unmapped HubSpot stage:{' '}
              <span className='font-semibold'>
                {stage.hubspot_label ?? '(unnamed)'}
              </span>
            </p>
            <p className='mt-0.5 text-xs text-utility-error-700'>
              This HubSpot stage is not linked to any Ahoy stage. Deals in this
              stage may not sync correctly. Use the stage editor to assign a
              mapping.
            </p>
          </div>
          <Badge type='pill-color' color='error'>
            {DRIFT_BADGE_LABEL.unmapped}
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className={`px-4 py-2.5 ${STAGE_ROW_GRID}`}>
      {/* Ahoy stage */}
      <div className='flex min-w-0 items-center gap-2'>
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
            hasDrift
              ? 'bg-[var(--color-fg-warning-primary)]'
              : 'bg-[var(--color-fg-success-primary)]'
          }`}
          aria-hidden='true'
        />
        <span className='truncate text-sm font-medium text-[var(--color-text-primary)]'>
          {stage.ahoy_name ?? '—'}
        </span>
        {stage.ahoy_order !== null && (
          <span className='shrink-0 text-xs text-[var(--color-text-tertiary)]'>
            #{stage.ahoy_order + 1}
          </span>
        )}
      </div>

      {/* HubSpot stage override select */}
      <div className='mt-2 min-w-0 sm:mt-0'>
        <span className={MOBILE_LABEL_CLASS}>HubSpot stage</span>
        {stage.ahoy_entity_id ? (
          <NativeSelect
            aria-label={`HubSpot stage for ${stage.ahoy_name ?? 'this stage'}`}
            options={pipelineStageOptions}
            value={stage.hubspot_stage_id ?? undefined}
            onChange={handleSelectChange}
            disabled={isSaving}
            selectClassName={SELECT_CLASS}
          />
        ) : (
          <p className='text-sm text-[var(--color-text-tertiary)]'>
            {stage.hubspot_label ?? '(archived)'}
          </p>
        )}
      </div>

      {/* Drift badge */}
      <div className='mt-2 sm:mt-0'>
        {hasDrift && (
          <Badge type='pill-color' color={DRIFT_BADGE_COLOR[stage.drift]}>
            {DRIFT_BADGE_LABEL[stage.drift]}
          </Badge>
        )}
      </div>
    </div>
  );
}

function renderPipelineStatusBadge(unmappedCount: number, driftCount: number) {
  if (unmappedCount > 0) {
    return (
      <Badge type='pill-color' color='error'>
        {unmappedCount} unmapped
      </Badge>
    );
  }
  if (driftCount > 0) {
    return (
      <Badge type='pill-color' color='warning'>
        {driftCount} drift{driftCount > 1 ? 's' : ''}
      </Badge>
    );
  }
  return (
    <Badge type='pill-color' color='success'>
      All matched
    </Badge>
  );
}

interface PipelineBlockProps {
  report: PipelineMappingReport;
  isSaving: boolean;
  onUpdate: (ahoyEntityId: string, hubspotStageId: string) => void;
}

function PipelineBlock({ report, isSaving, onUpdate }: PipelineBlockProps) {
  const stageOptions = useMemo(
    () =>
      report.stages
        // Only live HubSpot stages are valid remap targets. Archived stages
        // (drift === 'archived') no longer exist in HubSpot, so the update
        // endpoint rejects them with a 400 — never offer them as options.
        .filter(
          (s): s is PipelineStageMappingRow & { hubspot_stage_id: string } =>
            s.hubspot_stage_id !== null && s.drift !== 'archived'
        )
        .map(s => ({
          value: s.hubspot_stage_id,
          label: s.hubspot_label ?? '(archived)',
        })),
    [report.stages]
  );

  const unmappedCount = useMemo(
    () => report.stages.filter(s => s.drift === 'unmapped').length,
    [report.stages]
  );

  const driftCount = useMemo(
    () => report.stages.filter(s => s.drift !== 'ok').length,
    [report.stages]
  );

  return (
    <div className='overflow-hidden rounded-lg border border-[var(--color-border-secondary)]'>
      {/* Pipeline header */}
      <div className='flex items-center justify-between gap-3 border-b border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-4 py-3'>
        <div className='min-w-0'>
          <p className='text-sm font-semibold text-[var(--color-text-primary)]'>
            {report.ahoy_name ?? report.hubspot_label ?? '(archived pipeline)'}
          </p>
          {report.ahoy_name &&
            (report.hubspot_label ?? report.ahoy_name) !== report.ahoy_name && (
              <p className='text-xs text-[var(--color-text-tertiary)]'>
                HubSpot: {report.hubspot_label ?? '(archived pipeline)'}
              </p>
            )}
        </div>
        <div className='flex shrink-0 items-center gap-2'>
          {renderPipelineStatusBadge(unmappedCount, driftCount)}
        </div>
      </div>

      {/* Column headers (desktop only) */}
      <div
        className={`hidden border-b border-[var(--color-border-secondary)] bg-[var(--color-bg-tertiary)] px-4 py-2 sm:grid ${STAGE_ROW_GRID}`}
      >
        <span className='text-xs font-medium text-[var(--color-text-tertiary)]'>
          Ahoy stage
        </span>
        <span className='text-xs font-medium text-[var(--color-text-tertiary)]'>
          HubSpot stage
        </span>
        <span className='text-xs font-medium text-[var(--color-text-tertiary)]'>
          Status
        </span>
      </div>

      {/* Stage rows */}
      <div className='divide-y divide-[var(--color-border-secondary)]'>
        {report.stages.map(stage => (
          <StageRow
            key={stage.hubspot_stage_id}
            stage={stage}
            pipelineStageOptions={stageOptions}
            isSaving={isSaving}
            onUpdate={onUpdate}
          />
        ))}
        {report.stages.length === 0 && (
          <p className='px-4 py-3 text-sm text-[var(--color-text-secondary)]'>
            No stages found for this pipeline.
          </p>
        )}
      </div>
    </div>
  );
}

// --- Main component ---

export function PipelineStageMappingManager({
  orgId,
}: PipelineStageMappingManagerProps) {
  const { mappings, updateMapping } = usePipelineStageMappings(orgId);

  const pipelines = useMemo(
    () => mappings.data?.pipelines ?? EMPTY_PIPELINES,
    [mappings.data]
  );

  const handleUpdate = useCallback(
    (ahoyEntityId: string, hubspotStageId: string) => {
      updateMapping.mutate({
        ahoy_stage_entity_id: ahoyEntityId,
        hubspot_stage_id: hubspotStageId,
      });
    },
    [updateMapping]
  );

  const isSaving = updateMapping.isPending;
  const isSaveSuccess = updateMapping.isSuccess;

  return (
    <div className='space-y-5'>
      <div className='flex items-start justify-between gap-4'>
        <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
          Pipeline &amp; stage mappings
        </h3>
        <div className='min-h-[20px]'>
          {isSaving && (
            <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-secondary)]'>
              <LoadingIndicator size='xs' inline />
              Saving…
            </span>
          )}
          {!isSaving && isSaveSuccess && (
            <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-success-primary)]'>
              <CheckCircle className='h-4 w-4' aria-hidden='true' />
              Saved
            </span>
          )}
        </div>
      </div>

      {updateMapping.isError && (
        <AlertFloating
          color='error'
          title="Couldn't save mapping"
          description='Please try again.'
        />
      )}

      {mappings.isLoading && (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          Loading pipeline mappings…
        </p>
      )}

      {mappings.error && !mappings.isLoading && (
        <AlertFloating
          color='error'
          title="We couldn't load pipeline mappings"
          description='Please refresh and try again.'
        />
      )}

      {!mappings.isLoading && !mappings.error && pipelines.length === 0 && (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          No pipeline mappings found. Connect HubSpot and complete setup to see
          your deal pipelines here.
        </p>
      )}

      {!mappings.isLoading && !mappings.error && pipelines.length > 0 && (
        <div className='space-y-4'>
          {pipelines.map(report => (
            <PipelineBlock
              key={report.hubspot_pipeline_id}
              report={report}
              isSaving={isSaving}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
